import { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { CheckCircle2, Clock, Search, TrendingUp, Building2 } from "lucide-react";
import { PersonSidebar } from "./PersonSidebar";
import { OrganizationSidebar } from "./OrganizationSidebar";
import * as api from "@/src/api/sdk.gen";
import type {
    AdminPaymentsDashboardResponse,
    ProviderOverview,
    RevenueTrendItem,
} from "@/src/api/types.gen";
import { PROVIDERS, Provider } from "@/lib/admin/payments-dashboard-shared";

const PROVIDER_LABEL: Record<string, string> = {
    hyperpay: "HyperPay",
    payfast: "PayFast",
    stripe: "Stripe",
};

const STATUS_STYLES: Record<string, string> = {
    completed: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    failed: "bg-red-50 text-red-700",
};

function formatMoney(amount: number, currency: string) {
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(
            amount,
        );
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
}

function formatPercent(value: number | null | undefined) {
    if (value == null) return "—";
    return `${(value * 100).toFixed(1)}%`;
}

function KpiCard({
    label,
    value,
    icon: Icon,
    tint,
}: {
    label: string;
    value: string;
    icon: React.ComponentType<{ className?: string }>;
    tint: string;
}) {
    return (
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tint}`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
                    <p className="text-lg font-bold text-slate-900">{value}</p>
                </div>
            </div>
        </div>
    );
}

function ProviderCard({ overview }: { overview: ProviderOverview }) {
    return (
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">{PROVIDER_LABEL[overview.provider] || overview.provider}</h3>
                <span className="text-xs font-bold text-slate-400">{overview.currency}</span>
            </div>
            <p className="mt-2 text-2xl font-bold text-slate-900">
                {formatMoney(overview.revenue, overview.currency)}
            </p>
            <p className="text-xs font-medium text-slate-500">
                revenue from {overview.paidCompleted} paid completion{overview.paidCompleted === 1 ? "" : "s"}
            </p>

            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 py-2">
                    <p className="text-sm font-bold text-emerald-700">{overview.paidCompleted}</p>
                    <p className="text-[10px] font-bold uppercase text-emerald-600">Paid</p>
                </div>
                <div className="rounded-lg bg-slate-100 py-2">
                    <p className="text-sm font-bold text-slate-600">{overview.zeroValueCompleted}</p>
                    <p className="text-[10px] font-bold uppercase text-slate-500">$0</p>
                </div>
                <div className="rounded-lg bg-amber-50 py-2">
                    <p className="text-sm font-bold text-amber-700">{overview.pending}</p>
                    <p className="text-[10px] font-bold uppercase text-amber-600">Pending</p>
                </div>
                <div className="rounded-lg bg-red-50 py-2">
                    <p className="text-sm font-bold text-red-700">{overview.failed}</p>
                    <p className="text-[10px] font-bold uppercase text-red-600">Failed</p>
                </div>
            </div>
            <p className="mt-3 text-xs font-medium text-slate-500">
                Success rate <span className="font-bold text-slate-700">{formatPercent(overview.successRate)}</span>
                <span className="text-slate-400"> (paid + $0 completions)</span>
            </p>
        </div>
    );
}

function ProviderTrendChart({
    title,
    dataKey,
    color,
    data,
}: {
    title: string;
    dataKey: "hyperpay" | "payfast" | "stripe";
    color: string;
    data: RevenueTrendItem[];
}) {
    return (
        <div className="rounded-lg border border-slate-100 p-3">
            <div className="mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                <h4 className="text-xs font-bold text-slate-700">{title}</h4>
            </div>
            <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#64748B", fontSize: 10 }}
                            minTickGap={20}
                        />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: "#64748B", fontSize: 10 }} width={40} />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: "#1e293b",
                                border: "none",
                                borderRadius: "8px",
                                color: "#fff",
                                fontSize: "12px",
                            }}
                        />
                        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default function PaymentsDashboardClient() {
    const [data, setData] = useState<AdminPaymentsDashboardResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [days, setDays] = useState(30);
    const [providerFilter, setProviderFilter] = useState<"all" | Provider>("all");
    const [statusFilter, setStatusFilter] = useState<"all" | "completed" | "pending" | "failed">("all");
    const [typeFilter, setTypeFilter] = useState<"all" | "b2b" | "individual">("all");
    const [page, setPage] = useState(1);
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
    const [selectedOrgSlug, setSelectedOrgSlug] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const [pageResetForSearch, setPageResetForSearch] = useState(search);
    if (search !== pageResetForSearch) {
        setPageResetForSearch(search);
        setPage(1);
    }

    const requestKey = `${days}:${providerFilter}:${statusFilter}:${typeFilter}:${page}:${search}`;
    const [fetchedKey, setFetchedKey] = useState<string | null>(null);
    if (requestKey !== fetchedKey && !loading) {
        setLoading(true);
        setError(null);
    }

    useEffect(() => {
        api.getPaymentsDashboardApiV1AdminPaymentsDashboardGet({
            query: {
                days,
                provider: providerFilter,
                status: statusFilter,
                type: typeFilter !== "all" ? typeFilter : undefined,
                page,
                q: search || undefined,
            },
        })
            .then(({ data: resData, error: apiErr }) => {
                if (apiErr || !resData) {
                    throw new Error("Failed to load payments dashboard");
                }
                setData(resData);
            })
            .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dashboard data"))
            .finally(() => {
                setFetchedKey(requestKey);
                setLoading(false);
            });
    }, [days, providerFilter, statusFilter, typeFilter, page, search, requestKey]);

    const totalPages = useMemo(() => {
        if (!data) return 1;
        return Math.max(1, Math.ceil(data.recent.total / data.recent.pageSize));
    }, [data]);

    if (loading && !data) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />
                ))}
            </div>
        );
    }

    if (error || !data) {
        return <div className="text-center py-12 text-slate-500">{error ?? "Failed to load dashboard data."}</div>;
    }

    return (
        <div className="space-y-8">
            {/* Top-line KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <KpiCard
                    label="Total transactions"
                    value={String(data.overview.totals.total)}
                    icon={TrendingUp}
                    tint="bg-indigo-50 text-indigo-600"
                />
                <KpiCard
                    label="Completed"
                    value={String(data.overview.totals.completed)}
                    icon={CheckCircle2}
                    tint="bg-emerald-50 text-emerald-600"
                />
                <KpiCard
                    label="Pending / Failed"
                    value={`${data.overview.totals.pending} / ${data.overview.totals.failed}`}
                    icon={Clock}
                    tint="bg-amber-50 text-amber-600"
                />
                <KpiCard
                    label="Success rate"
                    value={formatPercent(data.overview.totals.successRate)}
                    icon={CheckCircle2}
                    tint="bg-slate-100 text-slate-600"
                />
            </div>

            {/* Per-provider breakdown */}
            <div>
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-bold text-slate-900">By payment provider</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    <ProviderCard overview={data.overview.hyperpay} />
                    <ProviderCard overview={data.overview.payfast} />
                    <ProviderCard overview={data.overview.stripe} />
                </div>
            </div>

            {/* Revenue trend */}
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="mb-1 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">Completed revenue over time</h3>
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-600"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                </div>
                <p className="mb-4 text-xs font-medium text-slate-400">
                    Each provider settles in a different currency, so they get separate charts rather than
                    one shared plot where the two are never actually comparable numbers.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <ProviderTrendChart title="HyperPay (SAR)" dataKey="hyperpay" color="#4f46e5" data={data.trend} />
                    <ProviderTrendChart title="PayFast (PKR)" dataKey="payfast" color="#ec4899" data={data.trend} />
                    <ProviderTrendChart title="Stripe (USD)" dataKey="stripe" color="#0d9488" data={data.trend} />
                </div>
            </div>

            {/* Recent transactions */}
            <div className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
                    <h3 className="text-sm font-bold text-slate-900">Recent transactions</h3>
                    <div className="flex flex-wrap gap-2">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Email, checkout ID, HyperPay ref, plan..."
                                className="w-64 rounded-lg border border-slate-200 bg-white py-1 pl-8 pr-2 text-xs font-medium text-slate-700 placeholder:text-slate-400"
                            />
                        </div>
                        <select
                            value={providerFilter}
                            onChange={(e) => {
                                setPage(1);
                                setProviderFilter(e.target.value as "all" | Provider);
                            }}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-600"
                        >
                            <option value="all">All providers</option>
                            <option value="hyperpay">HyperPay</option>
                            <option value="payfast">PayFast</option>
                            <option value="stripe">Stripe</option>
                        </select>
                        <select
                            value={typeFilter}
                            onChange={(e) => {
                                setPage(1);
                                setTypeFilter(e.target.value as "all" | "b2b" | "individual");
                            }}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-600"
                        >
                            <option value="all">All types</option>
                            <option value="b2b">B2B Corporate</option>
                            <option value="individual">Individual</option>
                        </select>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setPage(1);
                                setStatusFilter(e.target.value as "all" | "completed" | "pending" | "failed");
                            }}
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-slate-600"
                        >
                            <option value="all">All statuses</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                <th className="px-4 py-2">Checkout ID</th>
                                <th className="px-4 py-2">Buyer / Organization</th>
                                <th className="px-4 py-2">Item / Course</th>
                                <th className="px-4 py-2">Provider</th>
                                <th className="px-4 py-2">Brand</th>
                                <th className="px-4 py-2">Amount</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {data.recent.rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                                        No transactions match these filters.
                                    </td>
                                </tr>
                            ) : (
                                data.recent.rows.map((row) => (
                                    <tr key={row.id}>
                                        <td className="px-4 py-2 font-mono text-xs text-slate-500">
                                            {row.checkoutId}
                                            {row.couponCode ? (
                                                <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                                                    {row.couponCode}
                                                </span>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-2 text-slate-700">
                                            {row.userEmail ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedEmail(row.userEmail || null)}
                                                    className="text-left font-medium text-indigo-600 hover:underline cursor-pointer block"
                                                >
                                                    {row.userEmail}
                                                </button>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}

                                            {row.orgSlug && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedOrgSlug(row.orgSlug || null)}
                                                    className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[11px] font-semibold transition cursor-pointer border border-indigo-200/60"
                                                    title={`View ${row.companyName || row.orgSlug} lifetime spending and courses`}
                                                >
                                                    <Building2 className="h-3 w-3 text-indigo-500 shrink-0" />
                                                    <span className="truncate max-w-[150px]">
                                                        {row.companyName || row.orgSlug}
                                                    </span>
                                                </button>
                                            )}
                                            {row.purchaseOrderNumber && (
                                                <span className="text-[10px] text-slate-400 font-mono block mt-0.5">
                                                    PO: {row.purchaseOrderNumber}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-slate-700">
                                            <div>
                                                <p className="font-medium text-slate-900">{row.planName ?? "—"}</p>
                                                {row.seatLimit ? (
                                                    <span className="inline-block mt-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-1.5 py-0.5 text-[10px] font-bold">
                                                        {row.seatLimit} Seats Tier
                                                    </span>
                                                ) : row.itemType === "b2b_credits" ? (
                                                    <span className="inline-block mt-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200/60 px-1.5 py-0.5 text-[10px] font-bold">
                                                        AI Credits Pack
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-slate-700">
                                            {row.provider ? PROVIDER_LABEL[row.provider] ?? row.provider : "—"}
                                        </td>
                                        <td className="px-4 py-2 text-slate-700">{row.brand ?? "—"}</td>
                                        <td className="px-4 py-2 font-bold text-slate-900">
                                            {row.currency ? formatMoney(row.amount, row.currency) : row.amount}
                                            {row.zeroValueLabel ? (
                                                <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                                                    {row.zeroValueLabel}
                                                </span>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span
                                                className={`rounded-full px-2 py-1 text-xs font-bold ${
                                                    STATUS_STYLES[row.status ?? ""] ?? "bg-slate-100 text-slate-600"
                                                }`}
                                            >
                                                {row.status ?? "unknown"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-slate-500">
                                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between border-t border-slate-200 p-4 text-xs font-bold text-slate-500">
                    <span>
                        Page {data.recent.page} of {totalPages} &middot; {data.recent.total} total
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-slate-50"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed hover:bg-slate-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            <PersonSidebar email={selectedEmail} onClose={() => setSelectedEmail(null)} />
            <OrganizationSidebar orgSlug={selectedOrgSlug} onClose={() => setSelectedOrgSlug(null)} />
        </div>
    );
}
